import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { catalogApi } from '../lib/api';
import { Search, Filter, PackageOpen } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PRODUCT_TYPE_LABEL, PRODUCT_STATUS_LABEL, t } from '../lib/catalogI18n';

const STATUS_STYLE = {
  published: 'bg-emerald-100 text-emerald-800',
  draft: 'bg-amber-100 text-amber-800',
  withdrawn: 'bg-gray-200 text-gray-700',
};

export default function CatalogList() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [ftype, setFtype] = useState('');
  const [fformat, setFformat] = useState('');
  const [fstatus, setFstatus] = useState('');

  useEffect(() => {
    catalogApi.list().then(r => setProducts(r.data)).catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = products.filter(p => {
    const match = (p.title + ' ' + p.description + ' ' + p.code).toLowerCase().includes(q.toLowerCase());
    const okType = !ftype || p.product_type === ftype;
    const okFmt = !fformat || (p.formats || []).map(x => x.toLowerCase()).includes(fformat.toLowerCase());
    const okStatus = !fstatus || p.status === fstatus;
    return match && okType && okFmt && okStatus;
  });

  return (
    <div className="p-6 space-y-6" data-testid="catalog-list-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Catálogo de datos y servicios</h1>
        <p className="text-gray-600 mt-1">Productos y servicios de datos disponibles en el espacio sectorial de AEA.</p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-gray-400" />
          <Input placeholder="Buscar por nombre, código o descripción…" value={q}
            onChange={e => setQ(e.target.value)} data-testid="catalog-search-input" />
        </div>
        <div className="flex flex-wrap gap-3 text-sm">
          <label className="flex items-center gap-1">
            <Filter className="w-4 h-4 text-gray-400" /> Tipo:
            <select value={ftype} onChange={e => setFtype(e.target.value)}
              className="border rounded px-2 py-1" data-testid="catalog-filter-type">
              <option value="">Todos</option>
              <option value="contribution">Aportación</option>
              <option value="service">Servicio</option>
            </select>
          </label>
          <label className="flex items-center gap-1">Formato:
            <select value={fformat} onChange={e => setFformat(e.target.value)}
              className="border rounded px-2 py-1" data-testid="catalog-filter-format">
              <option value="">Todos</option>
              <option value="XLSX">XLSX</option>
              <option value="CSV">CSV</option>
              <option value="JSON">JSON</option>
            </select>
          </label>
          <label className="flex items-center gap-1">Estado:
            <select value={fstatus} onChange={e => setFstatus(e.target.value)}
              className="border rounded px-2 py-1" data-testid="catalog-filter-status">
              <option value="">Todos</option>
              <option value="published">Publicado</option>
              <option value="draft">Borrador</option>
              <option value="withdrawn">Retirado</option>
            </select>
          </label>
        </div>
      </div>

      {loading ? <div className="text-gray-500">Cargando…</div>
       : filtered.length === 0 ? (
         <div className="text-center py-16 text-gray-500" data-testid="catalog-empty">
           <PackageOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
           No se encontraron productos con esos filtros.
         </div>
       ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map(p => (
            <Link key={p.id} to={`/catalogo/${p.id}`}
                  data-testid={`catalog-card-${p.code}`}
                  className="bg-white rounded-lg border border-gray-200 p-5 hover:border-slate-800 hover:shadow-md transition">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-mono text-gray-500">{p.code}</p>
                  <h3 className="font-semibold text-gray-900 mt-1">{p.title}</h3>
                </div>
                <Badge className={STATUS_STYLE[p.status] || ''}>{t(PRODUCT_STATUS_LABEL, p.status)}</Badge>
              </div>
              <p className="text-sm text-gray-600 mt-2 line-clamp-3">{p.description}</p>
              <div className="flex items-center gap-2 mt-3 text-xs">
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                  {t(PRODUCT_TYPE_LABEL, p.product_type)}
                </span>
                {(p.formats || []).map(f => (
                  <span key={f} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{f}</span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
